import { useQuery } from "@tanstack/react-query";
import { PublicGameByGameId } from "./query-keys";
import { fetchPublicGame } from "./games-service";
import { useContacts, useGame } from "@seabrookstudios/pitch2table";

/**
 * @param {import("@seabrookstudios/types").GameId} gameId
 */
export const usePublicGame = (gameId) => {
  return useQuery({
    queryKey: PublicGameByGameId(gameId),
    queryFn: async () => {
      return fetchPublicGame(gameId);
    },
    staleTime: 0,
  });
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").GameDesigners[]} designers
 */
export const useDesigners = (designers = []) => {
  const userIds = designers.map(({ designerId }) => designerId);
  return useContacts(userIds);
};

const MAX_GAME_NAMES = 10;

/**
 * Batch resolve game names for up to 10 game IDs (same order).
 * Used for charts; hook count is fixed so IDs array must be capped at 10.
 *
 * @param {import("@seabrookstudios/types").GameId[] | null | undefined} gameIds length ≤ 10
 * @returns {string[]} names in same order as gameIds (empty string when missing)
 */
export const useGameNames = (gameIds) => {
  const padded = [...(gameIds || [])];
  while (padded.length < MAX_GAME_NAMES) padded.push(undefined);

  const id0 = padded[0];
  const id1 = padded[1];
  const id2 = padded[2];
  const id3 = padded[3];
  const id4 = padded[4];
  const id5 = padded[5];
  const id6 = padded[6];
  const id7 = padded[7];
  const id8 = padded[8];
  const id9 = padded[9];

  const r0 = useGame(/** @type {*} */ (id0));
  const r1 = useGame(/** @type {*} */ (id1));
  const r2 = useGame(/** @type {*} */ (id2));
  const r3 = useGame(/** @type {*} */ (id3));
  const r4 = useGame(/** @type {*} */ (id4));
  const r5 = useGame(/** @type {*} */ (id5));
  const r6 = useGame(/** @type {*} */ (id6));
  const r7 = useGame(/** @type {*} */ (id7));
  const r8 = useGame(/** @type {*} */ (id8));
  const r9 = useGame(/** @type {*} */ (id9));

  const results = [r0, r1, r2, r3, r4, r5, r6, r7, r8, r9];
  const ids = [id0, id1, id2, id3, id4, id5, id6, id7, id8, id9];

  return results
    .map((r, i) => {
      const id = ids[i];
      if (!id) return "";
      return r.data?.name ?? "";
    })
    .slice(0, gameIds?.length ?? 0);
};

